import PropTypes, { string } from "prop-types";
import { useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";
import { divide } from "theme";

import FnrTypography from "FnrTypography/FnrTypography";

import FnrButton from "UI/FnrButton3/FnrButton";
import FnrDeleteButton from "UI/FnrDeleteButton/FnrDeleteButton";
import FnrDragNDrop from "UI/FnrDragNDrop/FnrDragNDrop";
import ItemWithHandle from "UI/FnrDragNDrop/ItemWithHandle/ItemWithHandle";
import FnrFade from "UI/FnrFade/FnrFade";
import FnrTypeNSave from "UI/FnrTypeNSave/FnrTypeNSave";
import FnrHorizontalGap from "UI/commonContainers/FnrHorizontalGap/FnrHorizontalGap";
import FnrVerticalGap from "UI/commonContainers/FnrVerticalGap/FnrVerticalGap";

const SItemWithHandle = styled(ItemWithHandle)`
  & > div:last-child {
    transform: translateX(
      -10px
    ); /* sets the sort handle a little to the left */
  }
  ${divide}
`;

const SFnrHorizontalGap = styled(FnrHorizontalGap)`
  margin: 0 15%;
`;

const Thinner = styled("div")`
  width: 80%;
`;

const ItemComponent = ({
  onPointerDown,
  itemIdName,
  item: { displayValue, [itemIdName]: id, newOption },
  deleteOption,
  confirmText,
  disabled,
}) => {
  return (
    <SItemWithHandle onPointerDown={onPointerDown}>
      <SFnrHorizontalGap fullWidth>
        <FnrTypography>{displayValue}</FnrTypography>
        {!disabled && (
          <FnrDeleteButton
            aria-label={`delete-${displayValue}`}
            onClick={() => deleteOption(id)}
            data-cy="CreateOptions_OptionTrashButton"
            variant="iconButton"
            // request confirmation if the option was not just generated.
            confirmText={newOption ? false : confirmText}
          />
        )}
      </SFnrHorizontalGap>
    </SItemWithHandle>
  );
};

const ButtonContainer = styled("div")`
  display: flex;
  justify-content: center;
`;

const FnrCreateOptions = ({
  options = [],
  setOptions,
  itemIdName = "id",
  confirmText,
  disabled,
  noun = "option",
  ...rest
}) => {
  const [adding, setAdding] = useState(false);
  const buttonRef = useRef();
  const hasOptions = options.length > 0;
  const startAdding = () => setAdding(true);
  const stopAdding = () => {
    setAdding(false);
  };

  const addOption = (newValue) => {
    setOptions([
      ...options,
      { displayValue: newValue, [itemIdName]: Date.now(), newOption: true },
    ]);
    stopAdding();
  };

  const deleteOption = (id) => {
    setOptions(options.filter(({ [itemIdName]: elemId }) => id !== elemId));
  };

  useEffect(() => {
    if (!adding)
      setTimeout(() => {
        buttonRef.current?.focus();
      });
  }, [adding]);

  return (
    <FnrVerticalGap {...rest}>
      <FnrFade show={!hasOptions}>
        <FnrHorizontalGap centered>
          <FnrTypography color="textSecondary">
            Click the button below to add some {noun}s!
          </FnrTypography>
        </FnrHorizontalGap>
      </FnrFade>
      <FnrFade show={hasOptions}>
        <FnrHorizontalGap centered>
          <Thinner>
            <FnrDragNDrop
              ItemComponent={ItemComponent}
              items={options}
              itemIdName={itemIdName}
              itemHasDragTrigger
              deleteOption={deleteOption}
              onReorder={setOptions}
              confirmText={confirmText}
              disabled={disabled}
            />
          </Thinner>
        </FnrHorizontalGap>
      </FnrFade>
      <div>
        {adding && !disabled && (
          <div>
            {/* This FnrFade allows the autofocus to work. */}
            <FnrHorizontalGap centered>
              <Thinner>
                <FnrFade show={adding}>
                  <FnrTypeNSave
                    onSubmit={addOption}
                    autofocus
                    charLimit={50}
                    data-cy="ExtendedFields_AddOptionInput"
                  />
                </FnrFade>
              </Thinner>
            </FnrHorizontalGap>
          </div>
        )}
        {!adding && !disabled && (
          <div>
            <ButtonContainer>
              <FnrButton
                ref={buttonRef}
                color="blue"
                onClick={startAdding}
                data-cy="ExtendedFields_AddOptionButton"
              >
                <span>
                  {hasOptions ? `Add another ${noun}` : `Add ${noun}`}
                </span>
              </FnrButton>
            </ButtonContainer>
          </div>
        )}
      </div>
    </FnrVerticalGap>
  );
};

FnrCreateOptions.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      displayValue: PropTypes.string.isRequired,
      /** the id of the existing options. Note that this id property name is dynamic given itemIdName has a value */
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ),
  setOptions: PropTypes.func.isRequired,
  itemIdName: PropTypes.string,
  /** This confirmation text will appear for options that were not generated by this component. Leave undefined for no confirmation */
  confirmText: PropTypes.string,
  disabled: PropTypes.bool,
  noun: string,
};

export default FnrCreateOptions;
